"use client";

import { createContext, useContext, type ReactNode } from "react";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { useLabsIdentity, type LabsIdentity } from "./useLabsIdentity";

type LabsIdentityContextValue = {
  identity: LabsIdentity | null;
  loading: boolean;
  /** Re-resolves the Labs profile from the API (e.g. after the user changes their settings). */
  refresh: () => void;
};

const LabsIdentityContext = createContext<LabsIdentityContextValue>({
  identity: null,
  loading: true,
  refresh: () => {},
});

/** Resolves canonical Labs user (by email) once per session for all client pages. */
export function LabsIdentityProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const workOsUser = user
    ? {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      }
    : null;
  const { identity, loading, reload } = useLabsIdentity(workOsUser);
  return (
    <LabsIdentityContext.Provider value={{ identity, loading, refresh: reload }}>
      {children}
    </LabsIdentityContext.Provider>
  );
}

export function useLabsIdentityContext() {
  return useContext(LabsIdentityContext);
}

/** Canonical Labs user id, API headers, and database profile (admin, access flags). */
export function useLabsApiAuth() {
  const { identity, loading, refresh } = useLabsIdentityContext();
  return {
    labsUserId: identity?.labsUserId ?? null,
    headers: identity?.headers ?? {},
    profile: identity?.profile ?? null,
    loading,
    refresh,
  };
}
